import { useState } from "react"
import { Loader2 } from "lucide-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { 
  Button, 
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  useRepository 
} from "@city-sage/aurum-avis-labs-components"
import { toast } from "sonner"
import { Image as ImageType, Itinerary } from "@city-sage/core-client"
import { QueryKeys } from "../lib/QueryKeys"
import { ImageGallerySection } from "./ImageGallerySection"
import { uploadFileCompressed } from "../lib/imageUtils"

interface ImageEditingSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  itinerary: Itinerary
  searchWord?: string
}

export function ImageEditingSheet({ isOpen, onOpenChange, itinerary, searchWord }: ImageEditingSheetProps) {
  const { api } = useRepository()
  const queryClient = useQueryClient()
  
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [uploadedImage, setUploadedImage] = useState<ImageType | null>(null)
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null)
  const [isUsingUploadedImage, setIsUsingUploadedImage] = useState(false)

  // Query for preview images
  const { data: previewImages, isLoading: imagesLoading } = useQuery({
    queryKey: ['preview-images-title', searchWord],
    queryFn: async () => {
      if (!searchWord) return []
      return api.searchAndSavePreviewImagesByWord({ 
        searchAndSavePreviewImagesByWordRequest: { 
          word: searchWord, 
          count: 3
        } 
      })
    },
    enabled: !!searchWord && searchWord.length > 0 && isOpen,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Mutation to upload custom image
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      // Upload with compression
      return uploadFileCompressed(file, api, {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.8
      })
    },
    onSuccess: (data) => {
      setUploadedImage(data.url)
      setIsUsingUploadedImage(true)
      toast.success("Image uploaded successfully!")
    },
    onError: (error) => {
      toast.error("Failed to upload image. Please try again.")
      setUploadedImagePreview(null)
      console.error('Upload error:', error)
    }
  })

  // Mutation for updating itinerary image
  const updateImageMutation = useMutation({
    mutationFn: (imageUrl: string) => api.updateItinerary({
      id: itinerary.id,
      itinerary: {
        ...itinerary,
        imageUrl: imageUrl,
      }
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.details(itinerary.id) })
      onOpenChange(false)
      setUploadedImage(null)
      setUploadedImagePreview(null)
      setIsUsingUploadedImage(false)
      toast.success('Image updated successfully!')
    },
    onError: (error) => {
      toast.error('Failed to update image. Please try again.')
      console.error('Update image error:', error)
    }
  })

  // Handle custom image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setUploadedImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      
      // Upload the file
      uploadImageMutation.mutate(file)
    }
  }

  // Handle removing uploaded image
  const handleRemoveUploadedImage = () => {
    setUploadedImage(null)
    setUploadedImagePreview(null)
    setIsUsingUploadedImage(false)
  }

  // Handle saving selected image
  const handleSaveImage = () => {
    if (isUsingUploadedImage && uploadedImage) {
      // Use uploaded image URL
      updateImageMutation.mutate(uploadedImage.url)
    } else if (previewImages && previewImages[selectedImageIndex]) {
      // Use URL from the Image entity returned by the API
      const selectedImageEntity = previewImages[selectedImageIndex]
      updateImageMutation.mutate(selectedImageEntity.url)
    }
  }

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[85dvh] flex flex-col">
        <DrawerHeader className="px-6 py-4">
          <DrawerTitle className="text-lg font-semibold text-left">Edit Itinerary Image</DrawerTitle>
          <p className="text-sm text-gray-600 text-left mt-1">
            Choose a new image for your itinerary
          </p>
        </DrawerHeader>
        
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-6">
            {/* Image Gallery */}
            <ImageGallerySection
              previewImages={previewImages}
              imagesLoading={imagesLoading}
              selectedImageIndex={selectedImageIndex}
              uploadedImage={uploadedImage}
              uploadedImagePreview={uploadedImagePreview}
              isUsingUploadedImage={isUsingUploadedImage}
              isUploading={uploadImageMutation.isPending}
              onImageUpload={handleImageUpload}
              onPreviewImageSelect={(index) => {
                if (index === -1) {
                  setIsUsingUploadedImage(true)
                } else {
                  setSelectedImageIndex(index)
                  setIsUsingUploadedImage(false)
                }
              }}
              onRemoveUploadedImage={handleRemoveUploadedImage}
            />

            {/* Footer Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="flex-1"
                disabled={updateImageMutation.isPending}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveImage}
                disabled={updateImageMutation.isPending || (!isUsingUploadedImage && !previewImages?.length)}
                className="flex-1"
              >
                {updateImageMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Image'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
} 